import { useState, useEffect, useCallback } from 'react';
import type { ColumnDefinition } from '@/components/column-configurator-modal';

interface UseColumnPreferencesOptions {
  storageKey: string;
  defaultColumns: ColumnDefinition[];
}

interface UseColumnPreferencesReturn {
  columns: ColumnDefinition[];
  updateColumns: (newColumns: ColumnDefinition[]) => void;
  resetColumns: () => void;
  isLoading: boolean;
}

/**
 * Hook for managing column preferences with localStorage persistence
 * 
 * @param options - Configuration options
 * @param options.storageKey - Unique key for localStorage
 * @param options.defaultColumns - Default column configuration
 * @returns Column preferences state and update functions
 */
export function useColumnPreferences({
  storageKey,
  defaultColumns,
}: UseColumnPreferencesOptions): UseColumnPreferencesReturn {
  const [columns, setColumns] = useState<ColumnDefinition[]>(defaultColumns);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedColumns = JSON.parse(stored) as ColumnDefinition[];
        
        // Merge stored preferences with default columns to handle new columns
        const mergedColumns = defaultColumns.map((defaultCol) => {
          const storedCol = parsedColumns.find((col) => col.id === defaultCol.id);
          return storedCol ? { ...defaultCol, ...storedCol } : defaultCol;
        });

        // Add any new columns that don't exist in stored preferences
        const newColumns = defaultColumns.filter(
          (defaultCol) => !parsedColumns.some((col) => col.id === defaultCol.id)
        );

        setColumns([...mergedColumns, ...newColumns].sort((a, b) => a.order - b.order));
      } else {
        setColumns(defaultColumns);
      }
    } catch (error) {
      console.error('Failed to load column preferences:', error);
      setColumns(defaultColumns);
    } finally {
      setIsLoading(false);
    }
  }, [storageKey, defaultColumns]);

  // Update columns and persist to localStorage
  const updateColumns = useCallback(
    (newColumns: ColumnDefinition[]) => {
      try {
        setColumns(newColumns);
        localStorage.setItem(storageKey, JSON.stringify(newColumns));
      } catch (error) {
        console.error('Failed to save column preferences:', error);
      }
    },
    [storageKey]
  );

  // Reset to default columns
  const resetColumns = useCallback(() => {
    try {
      setColumns(defaultColumns);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to reset column preferences:', error);
    }
  }, [storageKey, defaultColumns]);

  return {
    columns,
    updateColumns,
    resetColumns,
    isLoading,
  };
}
